<template>
  <button class="webauthn-login proceed" @click="login" :disabled="isLoggingIn">
    <template v-if="isLoggingIn">
      <i class="fa fa-spinner fa-spin"></i>&nbsp;&nbsp;
    </template>
    <span>{{ isLoggingIn ? 'Logging in...' : 'Login with Device' }}</span>
  </button>
  <div v-if="error" class="error">{{ error }}</div>
  <div v-if="success" class="success">{{ success }}</div>
</template>

<script>
import { startAuthentication } from '@simplewebauthn/browser';
import api from '@/services/api';

export default {
  name: 'WebAuthnLogin',
  props: {
    brand_id: undefined,
    username: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoggingIn: false,
      error: '',
      success: ''
    };
  },
  methods: {
    async login() {
      if (!this.username || !this.username.trim()) {
        this.error = 'Email address is required';
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.username)) {
        this.error = 'Please enter a valid email address';
        return;
      }
      
      this.isLoggingIn = true;
      this.error = '';
      this.success = '';

      try {
        // Get authentication options from server
        const optionsResponse = await api.getAuthenticationOptions(this.username, this.brand_id);

        // Start authentication process in browser
        const assertionResponse = await startAuthentication(optionsResponse.data);

        // Send response to server for verification
        const verificationResponse = await api.verifyAuthentication(this.username, assertionResponse, this.brand_id);

        if (verificationResponse.data.verified) {
          this.success = 'Login successful!';
          
          // Store authToken in secure cookie
          this.$cookie.setCookie('authToken', verificationResponse.data.authToken, {
            expires: '7d',
            secure: true,
            httpOnly: false,
            sameSite: 'Strict'
          });
          
          // If uuid is provided, fetch user details
          if (verificationResponse.data.uuid) {
            await this.handleUserLogin(verificationResponse.data.uuid);
          }
          
          this.$emit('login-complete', verificationResponse.data.authToken);
        } else {
          this.error = 'Login failed. Please try again.';
          this.$emit('login-error', this.error);
        }
      } catch (error) {
        console.error(error);
        
        // Extract error message with priority for rate limiting and account lockout
        let errorMessage = 'Login failed';
        
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || error.response.data.error || errorMessage;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.error = errorMessage;
        this.$emit('login-error', this.error);
      } finally {
        this.isLoggingIn = false;
      }
    },
    
    async handleUserLogin(uuid) {
      try {
        // Use the fetchUserDetails function from parent component
        this.$emit('fetch-user-details', uuid);
      } catch (error) {
        console.error('Error handling user login:', error);
      }
    }
  }
};
</script>

<style scoped>
.error {
  color: red;
  margin-bottom: 10px;
  width: 100%;
}
.success {
  color: green;
  margin-bottom: 10px;
  width: 100%;
}
button {
  background-color: #42b983;
  color: white;
  border: none;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 4px;
}
button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

button.proceed {
  position: relative;
  width: 100%;
  flex: 1;
  padding: 1em 2rem;
  display: block;
  font-family: var(--details_buttons_primary_font_family, inherit);
  font-size: var(--details_buttons_primary_font_size, 1rem);
  font-weight: var(--details_buttons_primary_font_weight, inherit);
  background: var(--details_buttons_primary_background, #034a94);
  color: var(--details_buttons_primary_font_color, #fff);
  border: var(--details_buttons_primary_border, none);
  border-radius: var(--details_buttons_primary_border_radius, 0.25rem);
}
</style>
